import moment, { Moment } from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

type DefaultValue<T> = T | (() => T);

const SQL_MIN_YEAR = 1753;

/**
 * A half-typed date arrives from the picker as an invalid Moment. Writing it to the URL
 * would snap the field back to the default mid-typing, so pages only write usable dates.
 */
export const isUsableDate = (date?: Moment | null): date is Moment =>
	!!date?.isValid() && date.year() >= SQL_MIN_YEAR;

export interface UrlParam<T> {
	/** Returns undefined when the raw value cannot be parsed. */
	parse: (raw: string) => T | undefined;
	serialize: (value: NonNullable<T>) => string;
	defaultValue: DefaultValue<T>;
	/** Whether null is a real value ("All") rather than "use the default". */
	nullable: boolean;
	/**
	 * A default that depends on when the page was opened (e.g. "today"). It is always
	 * written, so a copied link keeps meaning the same range the next day.
	 */
	volatile: boolean;
}

// biome-ignore lint/suspicious/noExplicitAny: a schema mixes params of every value type
type Schema = Record<string, UrlParam<any>>;

export type UrlStateValues<S extends Schema> = {
	[K in keyof S]: S[K] extends UrlParam<infer T> ? T : never;
};

type Patch<S extends Schema> = Partial<UrlStateValues<S>>;

const resolveDefault = <T>(value: DefaultValue<T>): T =>
	typeof value === "function" ? (value as () => T)() : value;

const parseInteger = (raw: string) =>
	/^-?\d+$/.test(raw) ? Number(raw) : undefined;

const parseBoolean = (raw: string) =>
	raw === "1" || raw === "true"
		? true
		: raw === "0" || raw === "false"
			? false
			: undefined;

const parseDate = (raw: string, momentFn: typeof moment = moment) => {
	const parsed = momentFn(raw, "YYYY-MM-DD", true);
	return isUsableDate(parsed) ? parsed : undefined;
};

function int(): UrlParam<number | null>;
function int(defaultValue: DefaultValue<number>): UrlParam<number>;
// biome-ignore lint/suspicious/noExplicitAny: the overloads above carry the real types
function int(defaultValue: DefaultValue<number | null> = null): UrlParam<any> {
	return {
		parse: parseInteger,
		serialize: String,
		defaultValue,
		nullable: defaultValue === null,
		volatile: false,
	};
}

function string(): UrlParam<string | null>;
function string(defaultValue: DefaultValue<string>): UrlParam<string>;
// biome-ignore lint/suspicious/noExplicitAny: the overloads above carry the real types
function string(
	defaultValue: DefaultValue<string | null> = null,
): UrlParam<any> {
	return {
		parse: (raw: string) => raw,
		serialize: (value: string) => value,
		defaultValue,
		nullable: defaultValue === null,
		volatile: false,
	};
}

function oneOf<T extends string>(values: readonly T[]): UrlParam<T | null>;
function oneOf<T extends string>(
	values: readonly T[],
	defaultValue: DefaultValue<T>,
): UrlParam<T>;
function oneOf<T extends string>(
	values: readonly T[],
	defaultValue: DefaultValue<T | null> = null,
	// biome-ignore lint/suspicious/noExplicitAny: the overloads above carry the real types
): UrlParam<any> {
	return {
		parse: (raw: string) =>
			values.includes(raw as T) ? (raw as T) : undefined,
		serialize: (value: T) => value,
		defaultValue,
		nullable: defaultValue === null,
		volatile: false,
	};
}

export const urlParam = {
	int,
	string,
	oneOf,
	bool: (defaultValue: DefaultValue<boolean>): UrlParam<boolean> => ({
		parse: parseBoolean,
		serialize: (value) => (value ? "1" : "0"),
		defaultValue,
		nullable: false,
		volatile: false,
	}),
	/**
	 * Calendar date as YYYY-MM-DD, with a default that is usually relative to today.
	 * Pages that build their dates with a time zone aware moment pass their own factory,
	 * so a parsed date is the same kind of object as the default.
	 */
	date: (
		defaultValue: DefaultValue<Moment>,
		options: { moment?: typeof moment } = {},
	): UrlParam<Moment> => ({
		parse: (raw) => parseDate(raw, options.moment),
		serialize: (value) => value.format("YYYY-MM-DD"),
		defaultValue,
		nullable: false,
		volatile: true,
	}),
};

/**
 * Drops a URL value that is not among the options, once they have loaded. Without this a
 * shared link keeps filtering by an id the page cannot show, and the control reads as if
 * nothing were selected.
 */
export const useDropUnknownValue = (
	value: string | number | null,
	optionsLoaded: boolean,
	allowed: readonly (string | number)[],
	drop: () => void,
) => {
	const allowedKey = allowed.join("\u0001");
	useEffect(() => {
		if (!optionsLoaded || value === null) return;
		if (!allowedKey.split("\u0001").includes(String(value))) drop();
	}, [value, optionsLoaded, allowedKey, drop]);
};

/**
 * A text filter that types locally and reaches the URL once the user stops, so a search box
 * does not push a router update per keystroke.
 */
export const useUrlText = (
	urlValue: string,
	write: (value: string) => void,
	delay = 300,
): [string, (value: string) => void] => {
	const [text, setText] = useState(urlValue);
	const settled = useRef(urlValue);
	const writeRef = useRef(write);
	writeRef.current = write;

	// The URL changed elsewhere (a link, Back, a Reset): follow it.
	useEffect(() => {
		if (urlValue === settled.current) return;
		settled.current = urlValue;
		setText(urlValue);
	}, [urlValue]);

	useEffect(() => {
		if (text === settled.current) return;
		const id = setTimeout(() => {
			settled.current = text;
			writeRef.current(text);
		}, delay);
		return () => clearTimeout(id);
	}, [text, delay]);

	return [text, setText];
};

/**
 * Filters and selection kept in the query string.
 *
 * - A key missing from the URL, or one that cannot be parsed, takes its default.
 * - For a nullable key whose default is not null, an empty key (`?standId=`) means an
 *   explicit null, so "All" survives a reload.
 * - Every write replaces the history entry, writes the whole schema so a copied link is
 *   self-contained, and leaves keys outside the schema untouched.
 */
export const useUrlState = <S extends Schema>(schema: S) => {
	const [searchParams, setSearchParams] = useSearchParams();

	const schemaRef = useRef(schema);
	const defaultsRef = useRef<UrlStateValues<S> | null>(null);
	const computeDefaults = () =>
		Object.fromEntries(
			Object.entries(schemaRef.current).map(([key, def]) => [
				key,
				resolveDefault(def.defaultValue),
			]),
		) as UrlStateValues<S>;
	if (defaultsRef.current === null) defaultsRef.current = computeDefaults();
	const defaults = defaultsRef.current;

	const readValues = useCallback(
		(params: URLSearchParams): UrlStateValues<S> =>
			Object.fromEntries(
				Object.entries(schemaRef.current).map(([key, def]) => {
					const raw = params.get(key);
					if (raw === null) return [key, defaults[key]];
					if (raw === "") return [key, def.nullable ? null : defaults[key]];
					const parsed = def.parse(raw);
					return [key, parsed === undefined ? defaults[key] : parsed];
				}),
			) as UrlStateValues<S>,
		[defaults],
	);

	const search = searchParams.toString();
	const values = useMemo(
		() => readValues(new URLSearchParams(search)),
		[search, readValues],
	);

	const hasUrlState = useMemo(() => {
		const params = new URLSearchParams(search);
		return Object.keys(schemaRef.current).some((key) => params.has(key));
	}, [search]);

	const setValues = useCallback(
		(patch: Patch<S> | ((current: UrlStateValues<S>) => Patch<S>)) => {
			// Read the live URL, not the render's copy, so two setters in a row compose.
			const current = new URLSearchParams(window.location.search);
			const currentValues = readValues(current);
			const next: Record<string, unknown> = {
				...currentValues,
				...(typeof patch === "function" ? patch(currentValues) : patch),
			};

			for (const [key, def] of Object.entries(schemaRef.current)) {
				const value = next[key];
				const defaultValue = defaults[key];
				if (value === null || value === undefined) {
					if (def.nullable && defaultValue !== null) current.set(key, "");
					else current.delete(key);
					continue;
				}
				const serialized = def.serialize(value);
				const isDefault =
					defaultValue !== null && def.serialize(defaultValue) === serialized;
				if (isDefault && !def.volatile) current.delete(key);
				else current.set(key, serialized);
			}

			setSearchParams(current, { replace: true });
		},
		[defaults, readValues, setSearchParams],
	);

	/** Back to the defaults, recomputed now, and a clean URL for this page's keys. */
	const resetValues = useCallback(() => {
		defaultsRef.current = computeDefaults();
		const current = new URLSearchParams(window.location.search);
		for (const key of Object.keys(schemaRef.current)) current.delete(key);
		setSearchParams(current, { replace: true });
	}, [setSearchParams]);

	/**
	 * Removes keys whose value turned out to be invalid (e.g. an id that is no longer
	 * offered). Unlike a user change it writes nothing else, so correcting a link does not
	 * pin the default dates into it.
	 */
	const dropValues = useCallback(
		(...keys: (keyof S & string)[]) => {
			const current = new URLSearchParams(window.location.search);
			for (const key of keys) current.delete(key);
			setSearchParams(current, { replace: true });
		},
		[setSearchParams],
	);

	return [
		values,
		setValues,
		{ hasUrlState, defaults, resetValues, dropValues },
	] as const;
};
